// dependencies
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { View, StyleSheet } from "react-native";

// components
import Text from "@cloudspire/legacy-resources/src/components/Text2";
import Link from "@cloudspire/legacy-resources/src/components/Link2";
import Separator from "@cloudspire/legacy-resources/src/components/Separator";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import Button from "@cloudspire/legacy-resources/src/components/Button2";
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid2";
import SpoilerPhone from "@cloudspire/legacy-resources/src/components/SpoilerPhone";
import SpoilerOwnerPhoneButton from "@cloudspire/legacy-resources/src/components/SpoilerOwnerPhoneButton";
import { IconMailO } from "@cloudspire/legacy-svg-icon-library";

// containers
import ReserveButtonContainer from "./ReserveButton";

// constants
import { CONTACT_TYPE_VALUE_TRAVELLER } from "@cloudspire/shared/constants/contact";

// contexts
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";
import { useRouters } from "@cloudspire/legacy-resources/src/contexts/routers";

// libraries
import { generateUri } from "@cloudspire/legacy-shared/src/libraries";

// helpers
import {
  roomMayContactOwner,
  roomIsNatureParent,
  roomHasChildren,
} from "@cloudspire/legacy-resources/src/helpers/room";
import {
  userHasEmail,
  userHasPhone,
  userGetPhone,
} from "@cloudspire/legacy-resources/src/helpers/user";
import bookingDelegationGetPhone from "@cloudspire/legacy-resources/src/helpers/booking/delegation/getPhone";
import { roomIsBookableOnline } from "@cloudspire/legacy-resources/src/helpers/room";

const styles = StyleSheet.create({
  center: {
    flexDirection: "row",
    justifyContent: "center",
  },
});

function BookingAsideActions(props) {
  const {
    mode,
    priceToRender,
    hasPricingEstimatePrice,
    hasBookingEstimatePrice,
    type,
    room,
    pricingEstimateResponse,
    bookingEstimateResponse,
    parameterList,
    onPressContactUs,
    onPressOwnerContactMail,
    onPressOwnerContactPhone,
    onPressContactPhone,
    onPressContactMail,
    onPressBooking,
  } = props;

  const { frontRouter } = useRouters();

  const intl = useIntl();

  const configuration = useConfiguration();

  const bookingEstimateLoading =
    (!bookingEstimateResponse?.data && !bookingEstimateResponse?.error) ||
    bookingEstimateResponse?.isValidating;
  const pricingEstimateLoading =
    (!pricingEstimateResponse?.data && !pricingEstimateResponse?.error) ||
    bookingEstimateResponse?.isValidating;

  const hasCreate =
    "room" === type &&
    ("booking" === priceToRender
      ? Boolean(bookingEstimateResponse?.data?.data)
      : Boolean(pricingEstimateResponse?.data?.data) &&
        hasPricingEstimatePrice);

  const shouldRenderBookingButton =
    "plain" === mode &&
    roomIsBookableOnline({ room, configuration }) &&
    hasCreate;

  const shouldRenderContactUsButton =
    (roomIsNatureParent({ room }) && !roomHasChildren({ room })) ||
    ("booking" === priceToRender && !hasBookingEstimatePrice && !hasCreate);

  const shouldRenderMessageButton = roomMayContactOwner({
    room,
    configuration,
  });

  const contactPhone =
    room?.manager &&
    room?.metas?.managerHandlesPhoneReception &&
    userHasPhone({ user: room.manager })
      ? userGetPhone({ user: room.manager })
      : userGetPhone({ user: room.customer });

  const managerOrOwnerHasEmail =
    userHasEmail({ user: room.customer }) ||
    userHasEmail({ user: room.manager });

  const shouldRenderPhoneButton =
    roomMayContactOwner({ room, configuration }) &&
    !bookingEstimateResponse?.error &&
    Boolean(contactPhone);

  const contactPageUrl = generateUri({
    router: frontRouter,
    name: "App.Action.Contact",
    parameters: {
      lang: intl.locale,
    },
    query: {
      ref: parameterList?.roomReference,
      checkin: parameterList?.checkin,
      checkout: parameterList?.checkout,
      capacity: parameterList?.adults ?? 1,
      type: CONTACT_TYPE_VALUE_TRAVELLER,
    },
  });

  const channelBookingPhone = bookingDelegationGetPhone({
    configuration,
    room,
  });

  return (
    <>
      {"room" === type && (
        <>
          <Stack marginTop={1} />

          {!pricingEstimateLoading && !bookingEstimateLoading && (
            <>
              <Row type="column" gutter={0.5}>
                {shouldRenderBookingButton && (
                  <Col>
                    <ReserveButtonContainer
                      room={room}
                      parameterList={parameterList}
                      onPressBooking={onPressBooking}
                    />
                  </Col>
                )}

                {shouldRenderContactUsButton && (
                  <Col>
                    <Button
                      appearance="primary"
                      label={intl.formatMessage({
                        defaultMessage: "Nous contacter",
                      })}
                      to={contactPageUrl}
                      onPress={onPressContactUs}
                    />
                  </Col>
                )}

                {shouldRenderMessageButton && (
                  <Col>
                    <Button
                      to={
                        managerOrOwnerHasEmail
                          ? generateUri({
                              router: frontRouter,
                              name: "App.Action.Message",
                              parameters: {
                                lang: intl.locale,
                              },
                              query: parameterList,
                            })
                          : contactPageUrl
                      }
                      appearance="primary"
                      label={intl.formatMessage({
                        defaultMessage: "Contacter le propriétaire",
                      })}
                      data-gtm="room-aside-contact-owner-email"
                      onPress={onPressOwnerContactMail}
                    />
                  </Col>
                )}

                {shouldRenderPhoneButton && (
                  <Col>
                    <SpoilerOwnerPhoneButton
                      data-gtm="room-aside-contact-owner-phone"
                      phone={contactPhone}
                      onPress={onPressOwnerContactPhone}
                    />
                  </Col>
                )}
              </Row>
            </>
          )}

          <Stack marginTop={1} />

          {"plain" === mode && (
            <>
              <Separator color={"#CFDADD"} />

              <Stack marginTop={0.625} />

              <div style={{ textAlign: "center" }}>
                <Text>
                  {intl.formatMessage({
                    defaultMessage: "Besoin d’aide pour réserver ?",
                  })}
                </Text>
              </div>

              <Stack marginTop={1} />

              <Row type="cell" maxColumns={channelBookingPhone ? 2 : 1}>
                {channelBookingPhone && (
                  <Col>
                    <View style={styles.center}>
                      <SpoilerPhone
                        data-gtm="room-aside-booking-phone"
                        phone={channelBookingPhone}
                        onPress={onPressContactPhone}
                      />
                    </View>
                  </Col>
                )}

                <Col>
                  <View style={styles.center}>
                    <Link
                      data-gtm="room-aside-booking-email"
                      onClick={onPressContactMail}
                      to={generateUri({
                        router: frontRouter,
                        name: "App.Action.Contact",
                        parameters: {
                          lang: intl.locale,
                        },
                        query: {
                          ref: parameterList?.roomReference,
                          checkin: parameterList?.checkin,
                          checkout: parameterList?.checkout,
                          capacity: parameterList?.adults ?? 1,
                        },
                      })}
                    >
                      <FormattedMessage defaultMessage="E-mail">
                        <IconMailO />
                        &nbsp;
                        {(chunks) => chunks}
                      </FormattedMessage>
                    </Link>
                  </View>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </>
  );
}

export default BookingAsideActions;
